import { useCallback, useEffect, useState } from "react";

import { fmtISODate, fmtISORange, leaveChargeWarnings } from "@core";

import type { EmployerView } from "../lib/employer";
import {
  decideLeave, decideOvertimeAsk, decideOvertimeWorked, loadQueues,
  type PendingLeave, type Queues,
} from "../lib/decisions";

/**
 * Everything waiting on an answer, in one place.
 *
 * Three queues that look alike and are not. Leave and an overtime ask are both
 * permission for something that has not happened. Overtime already worked is
 * not permission at all — the hours exist, the money is owed, and what the
 * employer is recording is whether they sanctioned it.
 *
 * That distinction is the whole reason this screen needs writing carefully.
 * An employer who reads "Decline" as "do not pay" would be wrong about the law
 * and wrong about what the button does, so the third panel says what declining
 * means before it offers the button, and the amount stays on screen after the
 * decision either way.
 */

const LEAVE_WORD: Record<string, string> = {
  annual: "Annual leave",
  sick: "Sick leave",
  family: "Family responsibility",
};

function hoursLabel(value: number): string {
  const whole = Math.floor(value);
  const mins = Math.round((value - whole) * 60);
  return mins === 0 ? `${whole}h` : `${whole}h ${String(mins).padStart(2, "0")}m`;
}

export function Decisions({ ctx, canApprove }: { ctx: EmployerView; canApprove: boolean }) {
  const [queues, setQueues] = useState<Queues | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const ids = ctx.employees.map((e) => e.id).join(",");

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    loadQueues(ids ? ids.split(",") : [], ctx.timezone).then((q) => {
      if (cancelled) return;
      setQueues(q);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [ids, ctx.timezone]);

  useEffect(load, [load]);

  const money = new Intl.NumberFormat("en-ZA", {
    style: "currency", currency: ctx.currency, minimumFractionDigits: 2,
  });

  async function run(key: string, fn: () => Promise<{ ok: boolean; reason?: string }>) {
    setBusy(key);
    setProblem(null);
    const r = await fn();
    setBusy(null);
    if (!r.ok) {
      setProblem({
        NOT_PERMITTED: "You are not set as an approver on this household, so that decision was not recorded.",
        ALREADY_DECIDED: "Somebody has already answered that one. Reloading.",
        NOT_FOUND: "That request no longer exists.",
        NETWORK: "Couldn't reach Shifty. Check your connection.",
      }[r.reason ?? "NETWORK"] ?? "That did not go through.");
    }
    load();
  }

  if (loading) return <div className="centered">Reading what is waiting…</div>;

  const total = (queues?.leave.length ?? 0) + (queues?.asks.length ?? 0) + (queues?.worked.length ?? 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Waiting on you</h1>
          <p>
            Leave to grant, overtime to sanction, and hours already worked that nobody has
            answered. {total === 0 ? "Nothing at the moment." : ""}
          </p>
        </div>
      </div>

      {problem ? <div className="notice error">{problem}</div> : null}

      {/* An approver is a right, not a role. Somebody without it can read the
          queue — knowing what is outstanding is not a privilege — but the
          buttons would only ever fail, so they are not offered. */}
      {!canApprove ? (
        <div className="notice warn">
          You can see what is waiting, but you are not set as an approver on this household,
          so you cannot answer any of it. An owner can change that in the app.
        </div>
      ) : null}

      {/* ---------- leave ---------- */}
      <div className="panel">
        <div className="panel-top">
          <div className="panel-title">Leave</div>
          <span className="panel-note">{queues?.leave.length ?? 0} waiting</span>
        </div>
        {queues?.leave.length === 0 ? (
          <div className="empty">No leave is waiting for an answer.</div>
        ) : (
          <table className="rows">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>Who</th>
                <th>Dates</th>
                <th>Type</th>
                <th>Days charged</th>
                <th style={{ paddingRight: 22 }} />
              </tr>
            </thead>
            <tbody>
              {queues?.leave.map((l) => (
                <LeaveRow
                  key={l.id}
                  leave={l}
                  canApprove={canApprove}
                  busy={busy === l.id}
                  onDecide={(status, override) =>
                    run(l.id, () => decideLeave(l.id, status, override))}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ---------- overtime asked for in advance ---------- */}
      <div className="panel">
        <div className="panel-top">
          <div className="panel-title">Overtime asked for</div>
          <span className="panel-note">{queues?.asks.length ?? 0} waiting</span>
        </div>
        {queues?.asks.length === 0 ? (
          <div className="empty">Nobody has asked to work late.</div>
        ) : (
          <table className="rows">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>Who</th>
                <th>Day</th>
                <th>Hours</th>
                <th style={{ paddingRight: 22 }} />
              </tr>
            </thead>
            <tbody>
              {queues?.asks.map((a) => (
                <tr key={a.id}>
                  <td style={{ paddingLeft: 22 }}>
                    <span className="who">{a.employeeName}</span>
                    {a.reason ? <div className="panel-note">{a.reason}</div> : null}
                  </td>
                  <td className="num">{fmtISODate(a.onDate)}</td>
                  <td className="num">{hoursLabel(a.hours)}</td>
                  <td style={{ paddingRight: 22 }}>
                    <Answer
                      disabled={!canApprove || busy === a.id}
                      onYes={() => run(a.id, () => decideOvertimeAsk(a.id, "approved"))}
                      onNo={() => run(a.id, () => decideOvertimeAsk(a.id, "declined"))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="panel-foot">
          <span className="panel-note">
            This is permission for a day that has not happened. The Act caps overtime at three
            hours a day, and Shifty will not send an ask for more than that.
          </span>
        </div>
      </div>

      {/* ---------- overtime already worked ---------- */}
      <div className="panel">
        <div className="panel-top">
          <div className="panel-title">Overtime already worked</div>
          <span className="panel-note">{queues?.worked.length ?? 0} waiting</span>
        </div>

        {/* Said before the buttons, not after. An employer pressing Decline in
            the belief that it saves them money would be wrong about the law and
            wrong about what the button does. */}
        <div className="notice warn" style={{ margin: "18px 22px 0" }}>
          <strong>These hours are owed either way.</strong> Declining records that you did not
          sanction the extra time — which is a conversation to have — but the work happened and
          the BCEA does not let an employer answer worked hours with nothing. The money stays on
          the payslip.
        </div>

        {queues?.worked.length === 0 ? (
          <div className="empty">No worked overtime is waiting for an answer.</div>
        ) : (
          <table className="rows">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>Who</th>
                <th>Day</th>
                <th>Worked</th>
                <th>Overtime</th>
                <th>Comes to</th>
                <th style={{ paddingRight: 22 }} />
              </tr>
            </thead>
            <tbody>
              {queues?.worked.map((w) => (
                <tr key={w.shiftId}>
                  <td style={{ paddingLeft: 22 }}><span className="who">{w.employeeName}</span></td>
                  <td className="num">{fmtISODate(w.date)}</td>
                  <td className="num">{w.clockIn}–{w.clockOut}</td>
                  <td className="num">{hoursLabel(w.overtime)}</td>
                  <td className="num">{money.format(w.amount)}</td>
                  <td style={{ paddingRight: 22 }}>
                    <Answer
                      yes="Sanctioned"
                      no="Not sanctioned"
                      disabled={!canApprove || busy === w.shiftId}
                      onYes={() => run(w.shiftId, () => decideOvertimeWorked(w.shiftId, "approved"))}
                      onNo={() => run(w.shiftId, () => decideOvertimeWorked(w.shiftId, "declined"))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="panel-foot">
          <span className="panel-note">
            Only the answer is recorded here. The clock-in and clock-out cannot be changed from
            any screen — the register is the one thing in Shifty that is not editable after the
            fact.
          </span>
        </div>
      </div>
    </>
  );
}

/**
 * One leave request, with the days open to argument.
 *
 * Shifty counts working days and excludes weekends and public holidays, which
 * is right far more often than not. But it is a count of dates, and a real
 * week has half days in it, mornings back at work, and days an employer simply
 * decides not to charge. The number is offered, not imposed: change it and the
 * warnings appear beside it, and Approve still works either way.
 */
function LeaveRow({ leave, canApprove, busy, onDecide }: {
  leave: PendingLeave;
  canApprove: boolean;
  busy: boolean;
  onDecide: (status: "approved" | "declined", override?: { days: number; note: string }) => void;
}) {
  const [days, setDays] = useState(String(leave.days));
  const [note, setNote] = useState("");

  const charged = Number(days);
  const valid = Number.isFinite(charged) && charged >= 0;
  const changed = valid && charged !== leave.days;

  const warnings = changed
    ? leaveChargeWarnings({
        computed: leave.days, charged, type: leave.type, note,
        remaining: leave.annualLeft,
      })
    : [];

  return (
    <tr>
      <td style={{ paddingLeft: 22 }}>
        <span className="who">{leave.employeeName}</span>
        {leave.reason ? <div className="panel-note">{leave.reason}</div> : null}
      </td>
      <td className="num">{fmtISORange(leave.startsOn, leave.endsOn)}</td>
      <td>{LEAVE_WORD[leave.type] ?? leave.type}</td>
      <td>
        <input
          className="days"
          inputMode="decimal"
          value={days}
          disabled={!canApprove || busy}
          onChange={(e) => setDays(e.target.value)}
          aria-label="Days to charge"
        />
        {changed ? (
          <div className="panel-note">Shifty counted {leave.days}</div>
        ) : null}
        {!valid ? (
          <div className="panel-note" style={{ color: "var(--red)" }}>Not a number</div>
        ) : null}
        {changed ? (
          <input
            className="why"
            placeholder="Why?"
            value={note}
            disabled={busy}
            onChange={(e) => setNote(e.target.value)}
            aria-label="Why the charge differs"
          />
        ) : null}
        {warnings.map((w) => (
          <div key={w.section + w.text} className="notice warn" style={{ marginTop: 8, marginBottom: 0 }}>
            <strong>{w.section}</strong> — {w.text}
          </div>
        ))}
      </td>
      <td style={{ paddingRight: 22 }}>
        <Answer
          disabled={!canApprove || busy || !valid}
          onYes={() => onDecide("approved", changed ? { days: charged, note } : undefined)}
          onNo={() => onDecide("declined")}
        />
      </td>
    </tr>
  );
}

function Answer({ onYes, onNo, disabled, yes = "Approve", no = "Decline" }: {
  onYes: () => void;
  onNo: () => void;
  disabled: boolean;
  yes?: string;
  no?: string;
}) {
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
      <button className="btn inline quiet" type="button" disabled={disabled} onClick={onNo}>
        {no}
      </button>
      <button className="btn inline" type="button" disabled={disabled} onClick={onYes}>
        {yes}
      </button>
    </div>
  );
}
